<template>
  <div class="staff-management">
    <v-layout row wrap>
      <v-flex md6 sm6 pa-2>
        <form @submit.prevent="searchAgents">
          <v-text-field
              solo
              hide-details
              v-model="searchKeywords"
              append-icon="fal fa-search"
              :rules="[v => !v || v.length >= 2 || 'Min 2 characters']"
              :disabled="apiInProgress"
              type="search"
              placeholder="Search by name, email, phone, or 7 digit ID"
              @click:append="searchAgentsClicked"
          />
        </form>
      </v-flex>
      <v-flex md2 sm3 pa-2>
        <v-select
            clearable
            hide-details
            :items="countries"
            label="Country"
            v-model="selectedCountryId"
        />
      </v-flex>
      <v-flex md4 sm3 pa-2 class="layout align-center justify-end">
        <v-btn
          v-if="hasUserType('superadmin')"
          class="mr-2"
          :disabled="apiInProgress"
          @click="openProfitCalculator"
        >
          Averages calc.
        </v-btn>
        <v-btn color="primary" v-if="hasUserType('admin') && !isOnlyInvestor" :disabled="apiInProgress" @click="createAgent()"><v-icon>fal fa-plus</v-icon> &nbsp; Create Agent</v-btn>
      </v-flex>
      <v-flex xs12 px-2 pb-2 mt-2 v-if="selectedAgentTab === 'agents' && hasUserType('admin') && !isOnlyInvestor">
        <v-btn small flat :disabled="!selectedPhotoIdCount || photoIdZipInProgress" @click="clearPhotoIdSelection">Clear</v-btn>
        <v-btn small :disabled="!selectedPhotoIdCount || photoIdZipInProgress" :loading="photoIdZipInProgress" @click="downloadPhotoIdZip('png')"><v-icon small left>fal fa-file-image</v-icon> Download ID cards (PNG zip)</v-btn>
        <v-btn small :disabled="!selectedPhotoIdCount || photoIdZipInProgress" :loading="photoIdZipInProgress" @click="downloadPhotoIdZip('pdf')"><v-icon small left>fal fa-file-pdf</v-icon> Download ID cards (PDF zip)</v-btn>
        <span v-if="selectedPhotoIdCount" class="caption">{{ selectedPhotoIdCount }} selected</span>
      </v-flex>
    </v-layout>
    <v-tabs icons-and-text @change="onTabChanged">
      <v-tabs-slider color="primary" />
      <v-tab ripple href="#agents">
        Agents {{selectedAgentTab !== 'agents' ? `(${totalAgents})` : ''}}
        <v-icon>fal fa-user</v-icon>
      </v-tab>
      <v-tab ripple href="#invited-agents" v-if="hasUserType('admin')">
        Invited Agents {{selectedAgentTab !== 'invited-agents' ? `(${totalInvitedAgents})` : ''}}
        <v-icon>fal fa-user-plus</v-icon>
      </v-tab>
      <v-tab ripple href="#archived-agents" v-if="hasUserType('admin')">
        Archived Agents {{selectedAgentTab !== 'archived-agents' ? `(${totalArchivedAgents})` : ''}}
        <v-icon>fal fa-user-times</v-icon>
      </v-tab>
      <v-tabs-items v-model="selectedAgentTab">
        <v-tab-item value="agents">
          <v-data-table
              v-if="selectedAgentTab === 'agents'"
              :headers="headers"
              :items="agents"
              :pagination.sync="pagination"
              :total-items="getTotal(selectedAgentTab)"
              :loading="apiInProgress"
              :rows-per-page-items="[10, 25, 50]"
              @update:pagination="onPagination"
              class="elevation-1"
              must-sort
          >
            <template v-slot:items="props">
              <tr>
                <td @click.stop>
                  <v-checkbox
                    v-if="hasUserType('admin') && !isOnlyInvestor"
                    class="mt-0"
                    hide-details
                    :input-value="isPhotoIdSelected(props.item)"
                    :disabled="!props.item.canIssuePhotoId || photoIdZipInProgress"
                    :title="props.item.photoIdDisabledReason"
                    @change="togglePhotoIdSelection(props.item, $event)"
                  />
                </td>
                <td class="layout justify-start">
                  <v-tooltip v-if="signedInUser && props.item.email === signedInUser.email" top>
                    <template v-slot:activator="{ on }">
                      <v-btn style="margin: 0" flat small icon><v-icon small class="info--text" v-on="on">far fa-hand-point-right</v-icon></v-btn>
                    </template>
                    <span>Current user signed in</span>
                  </v-tooltip>
                  <v-text-field
                    class="text-field--flat"
                    v-model="props.item.lastName"
                    hide-details
                  />
                </td>
                <td>
                  <v-text-field
                    class="text-field--flat"
                    v-model="props.item.firstName"
                    hide-details
                  />
                </td>
                <td>{{ props.item.phone }}</td>
                <td>
                  <div>{{ props.item.email }}</div>
                  <template v-if="hasUserType('admin')">
                    <v-chip
                      v-for="label in props.item.penaltyTodayLabels"
                      :key="label"
                      small
                      class="penalty-today-chip mt-1"
                    >{{ label }}</v-chip>
                  </template>
                </td>
                <td>{{ props.item.balanceLabel }}</td>
                <td>{{ props.item.dateCreatedLabel }}</td>
                <td class="justify-center layout px-0" @click.stop>
                  <v-btn
                    flat
                    icon
                    class="mx-0"
                    title="Edit agent"
                    :disabled="!props.item.canView"
                    @click="loadAgent(props.item)"
                  >
                    <v-icon small>fas fa-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="canViewAgentTransactions"
                    flat
                    icon
                    class="mx-0"
                    title="View transactions"
                    :disabled="!props.item.canView"
                    @click="loadAgentTransactions(props.item)"
                  >
                    <v-icon small>fal fa-money-check-alt</v-icon>
                  </v-btn>
                  <v-btn v-if="hasUserType('admin') && !isOnlyInvestor" flat icon :disabled="!props.item.isEditable || signedInUser.email === props.item.email" :loading="props.item.isArchiveInProgress"  @click="archiveAgent(props.item)">
                    <v-icon small>
                      fas fa-archive
                    </v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-tab-item>
        <v-tab-item value="invited-agents" v-if="hasUserType('admin')">
          <v-data-table
              v-if="selectedAgentTab === 'invited-agents'"
              :headers="invitationHeaders"
              :items="agents"
              :pagination.sync="pagination"
              :total-items="getTotal(selectedAgentTab)"
              :loading="apiInProgress"
              :rows-per-page-items="[10, 25, 50]"
              @update:pagination="onPagination"
              class="elevation-1"
              must-sort
          >
            <template v-slot:items="props">
              <tr :class="props.item.isExpired ? 'invited-user--expired' : ''">
                <td :title="props.item.lastName">
                  <v-text-field
                    class="text-field--flat"
                    v-model="props.item.lastName"
                    hide-details
                  />
                </td>
                <td :title="props.item.firstName">
                  <v-text-field
                    class="text-field--flat"
                    v-model="props.item.firstName"
                    hide-details
                  />
                </td>
                <td>{{ props.item.phone }}</td>
                <td :title="props.item.email">
                  <v-text-field
                    class="text-field--flat"
                    v-model="props.item.email"
                    hide-details
                  />
                </td>
                <td>{{ props.item.dateCreatedLabel }}</td>
                <td>
                  <div>{{ props.item.verificationTokenExpiryLabel }}</div>
                  <div v-if="props.item.isExpired"><small class="error--text">(Expired)</small></div>
                </td>
                <td class="justify-center layout px-0" @click.stop>
                  <v-btn
                    flat
                    icon
                    class="mx-0"
                    title="Edit agent"
                    :disabled="!props.item.canView"
                    @click="loadInvitedAgent(props.item)"
                  >
                    <v-icon small>fas fa-pencil</v-icon>
                  </v-btn>
                  <v-btn flat icon @click="resendAgentInvitation(props.item)" :disabled="!props.item.isEditable" title="Re-send invitation">
                    <v-icon small>
                      fas fa-envelope
                    </v-icon>
                  </v-btn> &nbsp; <v-btn flat icon @click="cancelInvitedAgent(props.item)" :disabled="!props.item.isEditable" title="Cancel invitation">
                  <v-icon small>
                    fas fa-trash-alt
                  </v-icon>
                </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-tab-item>
        <v-tab-item value="archived-agents" v-if="hasUserType('admin')">
          <v-data-table
              v-if="selectedAgentTab === 'archived-agents'"
              :headers="archivedHeaders"
              :items="agents"
              :pagination.sync="pagination"
              :total-items="getTotal(selectedAgentTab)"
              :loading="apiInProgress"
              :rows-per-page-items="[10, 25, 50]"
              @update:pagination="onPagination"
              class="elevation-1"
              must-sort
          >
            <template v-slot:items="props">
              <tr>
                <td :title="props.item.lastName">
                  <v-text-field
                    class="text-field--flat"
                    v-model="props.item.lastName"
                    hide-details
                  />
                </td>
                <td :title="props.item.firstName">
                  <v-text-field
                    class="text-field--flat"
                    v-model="props.item.firstName"
                    hide-details
                  />
                </td>
                <td>{{ props.item.phone }}</td>
                <td :title="props.item.email">
                  <v-text-field
                    class="text-field--flat"
                    v-model="props.item.email"
                    hide-details
                  />
                </td>
                <td>{{ props.item.balanceLabel }}</td>
                <td>{{ props.item.dateCreatedLabel }}</td>
                <td>{{ props.item.dateArchivedLabel }}</td>
                <td class="justify-center layout px-0" @click.stop>
                  <v-btn
                    flat
                    icon
                    class="mx-0"
                    title="Edit agent"
                    :disabled="!props.item.canView"
                    @click="loadArchivedAgent(props.item)"
                  >
                    <v-icon small>fas fa-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="canViewAgentTransactions"
                    flat
                    icon
                    class="mx-0"
                    title="View transactions"
                    :disabled="!props.item.canView"
                    @click="loadAgentTransactions(props.item)"
                  >
                    <v-icon small>fal fa-money-check-alt</v-icon>
                  </v-btn>
                  <v-btn flat icon :loading="props.item.isArchiveInProgress" :disabled="!props.item.isEditable" @click="undoArchiveAgent(props.item)">
                    <v-icon small>
                      fas fa-undo
                    </v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </v-tabs>
    <agent-topup-profit-dialog
      v-if="profitCalculatorOpen"
      v-model="profitCalculatorOpen"
      :currency-symbol="computedCurrency"
    />
    <div class="agent-photo-id-offscreen" v-if="bulkPhotoIdCard">
      <agent-photo-id-card
        ref="bulkPhotoIdCard"
        :role-label="bulkPhotoIdCard.roleLabel"
        :id-number="bulkPhotoIdCard.idNumber"
        :full-name="bulkPhotoIdCard.fullName"
        :photo-src="bulkPhotoIdCard.photoSrc"
      />
    </div>
  </div>
</template>

<script>
import src from './src'
export default src
</script>

<style scoped>
.agent-photo-id-offscreen {
  position: fixed;
  left: -12000px;
  top: 0;
}
.penalty-today-chip {
  display: flex;
  font-size: 11px;
}
</style>
